import { useCallback, useMemo } from 'react';
import _ from 'lodash';

import viewSettingsActions from '../../../actions/viewSettingsActions';
import { VIEW_MODES, SETTINGS_KEYS } from '../../../configs/viewSettings';
import { isSelfLinkField } from '../helpers/links';
import availableFieldTypes from '../helpers/availableFieldTypes';

// Ширина грида и колонок меняется протяжкой мыши, масштаб — колесом: события сыплются
// десятками, а сохранять нужно итог
const SAVE_DELAY = 500;

const debounced = (save, option) => _.debounce((value) => save(option, value), SAVE_DELAY);

/**
 * Сохранение настроек вида. Отдаёт готовые обработчики для тех настроек, что меняются
 * из тела диаграммы, и общий changeFieldOption для раскладки.
 */
const useViewSettings = ({ catalogId, viewId, fields }) => {
  const saveOption = useCallback(
    (option, value) => {
      viewSettingsActions.setOption({ catalogId, viewId, viewMode: VIEW_MODES.GANTT, option, value });
    },
    [catalogId, viewId],
  );

  const saveScale = useMemo(() => debounced(saveOption, SETTINGS_KEYS.GANTT_SCALE), [saveOption]);
  const saveGridWidth = useMemo(() => debounced(saveOption, SETTINGS_KEYS.GANTT_GRID_WIDTH), [saveOption]);
  const saveColumnWidths = useMemo(() => debounced(saveOption, SETTINGS_KEYS.GANTT_COLUMN_WIDTHS), [saveOption]);

  // режим грида меняется кнопкой, копить нечего
  const saveDisplayMode = useCallback((mode) => saveOption(SETTINGS_KEYS.GANTT_DISPLAY_MODE, mode), [saveOption]);

  const changeFieldOption = useCallback(
    (option) => (fieldId) => {
      if (fieldId) {
        const field = fields && fields.find((f) => f.get('id') === fieldId);
        if (!field || (option === SETTINGS_KEYS.GANTT_LINK_FIELD && !isSelfLinkField(field, catalogId))) return;
        if (option === SETTINGS_KEYS.GANTT_EMPLOYEE && !availableFieldTypes.employee.includes(field.get('type')))
          return;
      }
      saveOption(option, fieldId ? { type: 'field', fieldId } : null);
    },
    [fields, catalogId, saveOption],
  );

  const changeLinkDirection = useCallback(
    (direction) => {
      if (!['predecessors', 'successors'].includes(direction)) return;
      saveOption(SETTINGS_KEYS.GANTT_LINK_DIRECTION, direction);
    },
    [saveOption],
  );

  return { saveScale, saveGridWidth, saveColumnWidths, saveDisplayMode, changeFieldOption, changeLinkDirection };
};

export default useViewSettings;
